import { ProcessPage } from '@adapters/navigation/page';
import { RouteUrl } from '@adapters/navigation/route';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import { Logger } from '@core/model/domain/logger';
import { ParentProcessEvent, ProcessEventType, WindowResizeEvent } from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { filter, map } from 'rxjs';

export class AppVM {
  constructor(
    private readonly router: Router,
    private readonly storeService: StoreService,
    private readonly processEventService: ProcessEventService,
    private readonly logger: Logger
  ) {}

  onInit() {
    this.handleParentEvent();
    this.handleRouteNavigation();
    this.handlePageRefresh();
    this.handleBrowserBackAndForwardNavigation();
  }

  private handleBrowserBackAndForwardNavigation() {
    /*
      popstate is triggered when the state object from History API is 
      about to change, this happens when : 
        - history.forward, history.back, history.go 
        - browser back and forward button

      The last back button push before page leave doesn't trigger popstate,
      it's handled directly by beforeunload event
    */
    window.addEventListener('popstate', () => {
      const routeBeforeBrowserNavigation = this.storeService.getVariableOrThrow(
        'ROUTE_BEFORE_BROWSER_NAVIGATION'
      );

      if (routeBeforeBrowserNavigation === '/process') {
        this.handleProcessRouteBrowserNavigation();
      } else if (routeBeforeBrowserNavigation === '/error') {
        this.handleErrorRouteBrowserNavigation();
      }
    });
  }
  private handleErrorRouteBrowserNavigation() {
    /*
      Can't prevent user to go back to /process route. A workaround is to
      fake it via an infinite loop on /error. The infinite loop stays until
      the user as flushed all the previous states, then he leave the page
      by triggering beforeunload event
    */
    history.pushState(null, '', '/error');
    this.logger.logEvent('BROWSER BACK BUTTON PUSHED');
  }

  private handleProcessRouteBrowserNavigation() {
    const nextProcessPage = history.state;
    const processPageHistory = this.storeService.getVariableOrThrow('PROCESS_PAGE_HISTORY');
    const previousPage = processPageHistory[processPageHistory.length - 2];

    if (nextProcessPage === previousPage) {
      processPageHistory.pop();
      this.storeService.setVariable('PROCESS_PAGE_HISTORY', processPageHistory);
      this.logger.logEvent('BROWSER BACK BUTTON PUSHED');
    } else {
      processPageHistory.push(nextProcessPage);
      this.storeService.setVariable('PROCESS_PAGE_HISTORY', processPageHistory);
      this.logger.logEvent('BROWSER FORWARD BUTTON PUSHED');
    }

    this.storeService.setVariable('PROCESS_PAGE', nextProcessPage as ProcessPage);
  }

  private handleRouteNavigation() {
    /* 
      RouterEvent is type Event_2 which is wrong, map is used to
      force the right type https://github.com/angular/angular/issues/43124
    */
    this.router.events
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((event: any) => event.routerEvent as RouterEvent),
        filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          console.log(`Router navigation start to ${event.url}`);
        } else {
          const regex = /^\/[^/?]*/;

          const substringRouteOnly = event.url.match(regex);

          if (substringRouteOnly) {
            this.storeService.setVariable(
              'ROUTE_BEFORE_BROWSER_NAVIGATION',
              substringRouteOnly[0] as RouteUrl
            );
            console.log(`Router navigation end to ${event.url}`);
          }
        }
      });
  }

  private handlePageRefresh() {
    /*
      beforeunload gets triggered on : 
        - page leave (close tab and tab url redirection) 
        - page refresh
        - window.dispatchEvent  

      There is no way to differentiate them
    */
    window.addEventListener('beforeunload', () => {
      this.logger.logEvent('USER REFRESHED OR LEFT OPEN BANKING JOURNEY');
    });
  }

  private handleEvent(event: ParentProcessEvent) {
    if (event.type === ProcessEventType.WINDOW_RESIZE_EVENT) {
      this.handleWindowResizeEvent(event);
    }
  }

  private handleWindowResizeEvent(event: WindowResizeEvent) {
    window.parent.postMessage(`OPEN_BANKING_RESIZE_EVENT: ${event.data}`, '*');
  }

  private handleParentEvent() {
    this.processEventService.subscribe(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.handleEvent.bind(this)
    );
  }
}
